import { NativeFetcher } from '@/components/helpers/fetch'
import { session } from '@/components/helpers/session'
import { getSearchSpringSiteID } from 'libs/searchspring/searchspring-utils'
import { generateID } from '@/components/helpers/utils'
export const BEACON_PAYLOAD_TYPES = {
  RENDER: 'render',
  IMPRESSION: 'impression',
  CLICK: 'click'
}
const BEACON_PAYLOAD_CATEGORY = {
  INTERACTIONS: 'searchspring.recommendations.user-interactions'
}
export class SearchspringBeacon extends NativeFetcher {
  profile
  constructor(profile) {
    super()
    this.profile = profile
  }

  // Action methods
  async sendPayload(payload) {
    const endpoint = 'https://beacon.searchspring.io/beacon'
    return await this.post(endpoint, payload)
  }

  // String formatters - just avoids needing to log every payload type
  parseProfileType(type) {
    return `profile.${type}`
  }

  parseProductType(type) {
    return `profile.product.${type}`
  }

  // Context generators (reference internally consistent data across all events for this instance)
  generateEventContext() {
    return {
      type: 'product-recommendation',
      tag: this.profile
    }
  }

  generatePayloadContext() {
    const pageLoadId = session.pageLoadId.get()
    const userId = session.userId.get()
    const sessionId = session.sessionId.get()
    const siteId = getSearchSpringSiteID()
    return {
      pageLoadId,
      userId,
      sessionId,
      website: {
        trackingCode: siteId
      }
    }
  }

  // Generic event handlers
  generatePayload(type, payload) {
    const context = this.generatePayloadContext()
    return {
      ...payload,
      context,
      category: BEACON_PAYLOAD_CATEGORY.INTERACTIONS,
      type,
      id: generateID()
    }
  }

  generateEvent(event) {
    const context = this.generateEventContext()
    return {
      context,
      ...event
    }
  }

  // Specific event functions
  /**
   * Generates the formatted data to send a 'render' event to the Beacon API
   * Takes the full SS response to get correct data
   * https://docs.searchspring.com/reference/personalized-recommendations#render
   */
  generateRenderPayload(data) {
    if (!data?.results) return []
    const type = this.parseProfileType(BEACON_PAYLOAD_TYPES.RENDER)
    const parent = this.generatePayload(type, {
      event: this.generateEvent({
        profile: {
          tag: this.profile,
          seed: ['']
        }
      })
    })
    const children = data.results.map((result) => {
      const type = this.parseProductType(BEACON_PAYLOAD_TYPES.RENDER)
      return this.generatePayload(type, {
        pid: parent.id,
        event: this.generateEvent({
          product: {
            id: result.id,
            mappings: result.mappings
          }
        })
      })
    })
    return [parent, ...children]
  }

  /**
   * Generates the formatted data to send an 'impression' event to the Beacon API
   * Takes the full SS response to get correct data
   * https://docs.searchspring.com/reference/personalized-recommendations#impression
   */
  generateImpressionPayload(results) {
    if (!results?.results) return []
    const type = this.parseProfileType(BEACON_PAYLOAD_TYPES.IMPRESSION)
    const parent = this.generatePayload(type, {
      event: this.generateEvent({
        profile: {
          tag: this.profile,
          seed: ['']
        }
      })
    })
    const children = results.results.map((result) => {
      const type = this.parseProductType(BEACON_PAYLOAD_TYPES.IMPRESSION)
      return this.generatePayload(type, {
        pid: parent.id,
        event: this.generateEvent({
          product: {
            id: result.id,
            mappings: result.mappings
          }
        })
      })
    })
    return [parent, ...children]
  }

  /**
   * Generates the formatted data to send an 'click' event to the Beacon API
   * Takes the result of the card that was clicked only
   * https://docs.searchspring.com/reference/personalized-recommendations#clickimpression
   */
  generateClickPayload(result) {
    if (!result) return []
    const parent = this.generatePayload(this.parseProfileType(BEACON_PAYLOAD_TYPES.CLICK), {
      event: this.generateEvent({
        profile: {
          tag: this.profile,
          seed: ['']
        }
      })
    })
    const child = this.generatePayload(this.parseProductType(BEACON_PAYLOAD_TYPES.CLICK), {
      pid: parent.id,
      event: this.generateEvent({
        product: {
          id: result.id,
          mappings: result.mappings
        }
      })
    })
    return [parent, child]
  }
}
